import { Box, Chip, Grid, Skeleton, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useState } from "react";
import theme from "../../styles/styleTheme";
import styled from "styled-components";
import { isMobile } from "react-device-detect";
import { monthlyRentConverter } from "../../utils/enumConverter";
import { useTranslation } from "react-i18next";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import StraightenIcon from "@mui/icons-material/Straighten";
import KingBedOutlinedIcon from "@mui/icons-material/KingBedOutlined";
import ChairOutlinedIcon from "@mui/icons-material/ChairOutlined";
import ShowerOutlinedIcon from "@mui/icons-material/ShowerOutlined";
import TollOutlinedIcon from "@mui/icons-material/TollOutlined";
import { formatCurrency } from "../../utils/currency";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";

type PropertyDetailsDescriptionProps = {
  property: any;
};

const PropertyDetailsDescription = ({
  property,
}: PropertyDetailsDescriptionProps) => {
  const { t } = useTranslation();
  const [expanedDetails, setExpandedDetails] = useState(false);

  const isPastOrToday =
    !!property?.propertyDetails?.availableFromDate &&
    new Date(property?.propertyDetails?.availableFromDate).setHours(
      0,
      0,
      0,
      0
    ) <= new Date().setHours(0, 0, 0, 0);

  return (
    <Grid item xs={12} md={12} marginTop={1}>
      <>
        <Box style={{ marginTop: "1rem" }}>
          {property ? (
            <Typography variant="h1">
              {property.type.name === "Property" ? (
                <>
                  {formatCurrency(property?.propertyDetails?.minimumRent)} /{" "}
                  {t("dates.month")}
                </>
              ) : (
                <Typography variant="h1">
                  {property?.type?.name === "Collection" &&
                    property?.propertyCollectionDetails?.minPrice > 0 &&
                    property?.propertyCollectionDetails?.maxPrice > 0 && (
                      <>
                        {formatCurrency(
                          property?.propertyCollectionDetails?.minPrice
                        )}{" "}
                        -{""}
                        {formatCurrency(
                          property?.propertyCollectionDetails?.maxPrice
                        )}
                        / {t("dates.month")}
                      </>
                    )}
                  {property?.type?.name === "Collection" &&
                    (!property?.propertyCollectionDetails?.minPrice ||
                      !property?.propertyCollectionDetails?.maxPrice) && (
                      <>
                        {formatCurrency(property?.propertyDetails?.minimumRent)}{" "}
                        / {t("dates.month")}
                      </>
                    )}
                </Typography>
              )}
            </Typography>
          ) : (
            <Skeleton variant="text" width={150} height={33} />
          )}
        </Box>
        <Box style={{ marginTop: ".2rem" }}>
          {property ? (
            <Typography variant="body2" color="#888">
              <LocationOnOutlinedIcon fontSize="inherit" />
              {property?.propertyDetails?.streetAddress}
              {property?.propertyDetails?.zipcode &&
                `, ${property?.propertyDetails?.zipcode}`}
            </Typography>
          ) : (
            <Skeleton variant="text" width={150} height={33} />
          )}
        </Box>

        <Box style={{ marginTop: ".5rem", marginBottom: "1rem" }}>
          {property?.propertyDetails?.availableFromDate &&
            !property?.isRented && (
              <DetailChip
                icon={
                  <EventAvailableOutlinedIcon
                    style={{
                      color: theme.palette.primary.main,
                      fontSize: "1.2rem",
                    }}
                  />
                }
                label={
                  isPastOrToday
                    ? t("details.availableNow")
                    : property?.propertyDetails?.availableFromDate
                    ? (() => {
                        const date = new Date(
                          property?.propertyDetails?.availableFromDate
                        );
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = String(date.getMonth() + 1).padStart(
                          2,
                          "0"
                        );
                        const year = date.getFullYear();
                        return `${t(
                          "details.availableFrom"
                        )} ${day}-${month}-${year}`;
                      })()
                    : ""
                }
                sx={{
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.light,
                  marginBottom: isMobile ? ".5rem" : "0",
                }}
              />
            )}
          {property?.propertyFeatures?.livingArea && (
            <DetailChip
              icon={
                <StraightenIcon
                  style={{
                    color: theme.palette.primary.main,
                    fontSize: "1.2rem",
                  }}
                />
              }
              label={property?.propertyFeatures?.livingArea + " m²"}
              sx={{
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
                marginBottom: isMobile ? ".5rem" : "0",
              }}
            />
          )}
          {property?.propertyFeatures?.noOfBedrooms && (
            <DetailChip
              icon={
                <KingBedOutlinedIcon
                  style={{
                    color: theme.palette.primary.main,
                    fontSize: "1.2rem",
                  }}
                />
              }
              label={
                property?.propertyFeatures?.noOfBedrooms +
                " " +
                t("details.bedrooms") +
                (property?.propertyFeatures?.noOfBedrooms > 1 ? "s" : "")
              }
              sx={{
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
                marginBottom: isMobile ? ".5rem" : "0",
              }}
            />
          )}
          {property?.propertyFeatures?.noOfBathrooms && (
            <DetailChip
              icon={
                <ShowerOutlinedIcon
                  style={{
                    color: theme.palette.primary.main,
                    fontSize: "1.2rem",
                  }}
                />
              }
              label={
                property?.propertyFeatures?.noOfBathrooms +
                " " +
                t("details.bathrooms") +
                (property?.propertyFeatures?.noOfBathrooms > 1 ? "s" : "")
              }
              sx={{
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
                marginBottom: isMobile ? ".5rem" : "0",
              }}
            />
          )}

          {property?.propertyFeatures?.energyLabel && (
            <DetailChip
              icon={
                <EnergySavingsLeafOutlinedIcon
                  style={{
                    color: theme.palette.primary.main,
                    fontSize: "1.2rem",
                  }}
                />
              }
              sx={{
                fontSize: ".8em",
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
              }}
              label={
                property?.propertyFeatures?.energyLabel?.includes("+")
                  ? property.propertyFeatures?.energyLabel.substring(
                      0,
                      property.propertyFeatures?.energyLabel.lastIndexOf("+") +
                        1
                    )
                  : t(property?.propertyFeatures?.energyLabel)
              }
            />
          )}

          {property?.propertyFeatures?.furnishedDecorated === "Yes" && (
            <DetailChip
              icon={
                <ChairOutlinedIcon
                  style={{
                    color: theme.palette.primary.main,
                    fontSize: "1.2rem",
                  }}
                />
              }
              label={t("details.furnished")}
              sx={{
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
              }}
            />
          )}
          {property?.propertyFeatures?.includingFurniture === "Yes" && (
            <DetailChip
              label={t("details.furnishedDecorated")}
              icon={
                <ChairOutlinedIcon
                  style={{
                    color: theme.palette.primary.main,
                    fontSize: "1.2rem",
                  }}
                />
              }
              sx={{
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
              }}
            />
          )}
          {property?.propertyFeatures?.depositAmount && (
            <DetailChip
              icon={
                <TollOutlinedIcon
                  style={{
                    color: theme.palette.primary.main,
                    fontSize: "1.2rem",
                  }}
                />
              }
              label={
                t("details.deposit") +
                ": " +
                monthlyRentConverter(
                  property?.propertyFeatures?.depositAmount,
                  t
                )
              }
              sx={{
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
              }}
            />
          )}
        </Box>

        <Box
          style={{
            paddingLeft: ".3rem",
            marginBottom: ".5rem",
            paddingBottom: ".5rem",
            borderBottom: "2px solid #eee",
            marginTop: "2rem",
          }}
        >
          {property ? (
            <Typography variant="h3">{t("details.description")}</Typography>
          ) : (
            <Skeleton variant="text" width={150} height={33} />
          )}
        </Box>
        {property ? (
          <StyledDescription>
            <Typography
              style={{
                paddingLeft: ".3rem",
                maxHeight: expanedDetails ? "none" : "10.3rem",
                overflow: "hidden",
              }}
              variant="body1"
              dangerouslySetInnerHTML={{
                __html: property?.propertyDetails.description,
              }}
            ></Typography>
          </StyledDescription>
        ) : (
          <>
            <Skeleton variant="text" width={670} />
            <Skeleton variant="text" width={670} />
            <Skeleton variant="text" width={670} />
            <Skeleton variant="text" width={670} />
            <Skeleton variant="text" width={670} />
            <Skeleton variant="text" width={300} />
          </>
        )}
        {property?.propertyDetails.description.length > 650 && (
          <>
            <Box
              style={{
                marginTop: "-5rem",
                paddingTop: "5rem",
                paddingLeft: ".3rem",
                backgroundColor: "#fff",
                zIndex: "999 !important",
                color: theme.palette.primary.main,
                position: "relative",
                userSelect: "none",
                background: expanedDetails
                  ? ""
                  : "linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 10%, rgba(255,255,255,0) 100%)",
              }}
            />

            <Typography
              sx={{
                borderRadius: "0 0 16px 16px",
                margin: "0 -1rem",
                marginBottom: "-1rem",
                padding: ".5rem 0",
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: theme.palette.secondary.light,
                color: theme.palette.secondary.main,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.light,
                },
              }}
              variant="body1"
              onClick={() => {
                setExpandedDetails(!expanedDetails);
              }}
            >
              {expanedDetails ? (
                <>- {t("details.read_less")}</>
              ) : (
                <>+ {t("details.read_more")}</>
              )}
            </Typography>
          </>
        )}
      </>
    </Grid>
  );
};

export default PropertyDetailsDescription;

const DetailChip = styled(Chip)({
  backgroundColor: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  color: "#fff",
  fontWeight: 600,
  marginRight: ".5rem",
  marginTop: ".5rem",
});

const StyledDescription = styled(Box)`
  p {
    padding-bottom: 0.5rem;
    ul {
      padding-left: 2rem;
      margin: 0.8rem 0;
      li[data-type="taskItem"] {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
      }
    }
    ol {
      padding-left: 2rem;
      margin: 0.8rem 0;
    }
    h1 {
      margin-bottom: 0.5rem;
      font-size: 22px;
    }
    h2 {
      font-size: 20px;
    }
    h3 {
      font-size: 18px;
    }
    h4 {
      font-size: 16px;
    }
    hr {
      margin: 10px 0%;
    }
  }
`;
